/**
 * useFormsFlow - Form flow orchestration hook
 *
 * Task: T-P4-D2-F4b-7
 * Feature: P4-D2-F4 Web App Forms UI
 *
 * Derives the current form to show based on flowContext and locally-tracked completions.
 * This is a mini state machine: registration -> required forms sequence -> next step navigation.
 *
 * IMPORTANT: completedFormIds is tracked locally in DetailsPage state, NOT from flowContext.
 * This is because:
 * 1. flow doesn't expose collectedData.forms
 * 2. We cannot refetch session after submit (Telegram initData expires)
 * 3. Forms GET returns `submission` marker when already submitted - use that to populate
 *
 * Uses flowContext (from useFlowContext) as canonical source of:
 * - user.isRegistered
 * - registrationFormId
 * - context.service.requiredForms
 */

import { useMemo } from 'react';
import type { WebAppFlowContextResponse } from '../api/types';

// ============================================
// Types
// ============================================

export interface FormsFlowState {
  /** Whether user needs to complete registration form first */
  needsRegistration: boolean;
  /** Registration form ID (null if user is registered or no form exists) */
  registrationFormId: string | null;
  /** Required intake/waiver forms from service (sorted by sortOrder) */
  requiredForms: Array<{
    formId: string;
    formName: string;
    sortOrder: number;
    isRequired: boolean;
  }>;
  /** Current form ID to display (null if all complete or error state) */
  currentFormId: string | null;
  /** 1-indexed position for FormProgress (e.g., "Form 2 of 3") */
  currentFormIndex: number;
  /** Total number of forms in flow */
  totalForms: number;
  /** Whether we're in an error state (e.g., CUSTOM_FORMS without service) */
  isError: boolean;
  /** Error message to display if isError is true */
  errorMessage: string | null;
}

export interface UseFormsFlowOptions {
  /** Flow context from useFlowContext (canonical source of truth) */
  flowContext: WebAppFlowContextResponse | undefined;
  /** Locally-tracked completed form IDs (populated when form GET returns submission marker) */
  completedFormIds: Set<string>;
  /** Current flow step from flowContext (for defensive redirects) */
  currentStep?: string;
}

// ============================================
// Hook
// ============================================

/**
 * Derives the current form to show based on flowContext and locally-tracked completions.
 *
 * Flow logic:
 * 1. If user is unregistered AND registrationFormId exists AND not yet completed
 *    -> Show registration form
 * 2. Otherwise, iterate through service.requiredForms in sortOrder
 *    -> Skip forms already in completedFormIds
 *    -> Show first uncompleted form
 * 3. If all forms complete -> currentFormId is null (parent navigates away)
 *
 * Edge cases:
 * - REGISTRATION without service: totalForms = 1, just registration
 * - CUSTOM_FORMS without service: error state
 */
export function useFormsFlow({
  flowContext,
  completedFormIds,
  currentStep,
}: UseFormsFlowOptions): FormsFlowState {
  return useMemo(() => {
    // No flowContext yet - return empty state
    if (!flowContext) {
      return {
        needsRegistration: false,
        registrationFormId: null,
        requiredForms: [],
        currentFormId: null,
        currentFormIndex: 0,
        totalForms: 0,
        isError: false,
        errorMessage: null,
      };
    }

    const needsRegistration = !flowContext.user.isRegistered;
    const registrationFormId = flowContext.registrationFormId;
    const allForms = flowContext.context.service?.requiredForms ?? [];

    // Keep only the forms the client must actually fill: required, and not
    // already satisfied by a reusable earlier submission. The server skips the
    // same ids (FlowContext.user.reusableCompletedFormIds) when it advances the
    // flow, so the page and the engine agree form by form, not just on whether
    // the whole step exists (forms-flow-partial-reuse, 2026-09-04).
    const reusable = new Set(flowContext.user.reusableCompletedFormIds ?? []);
    const sortedForms = allForms
      .filter((form) => form.isRequired && !reusable.has(form.formId))
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Handle edge case: CUSTOM_FORMS step but no service context
    // This indicates data inconsistency - show error
    if (currentStep === 'CUSTOM_FORMS' && !flowContext.context.service) {
      return {
        needsRegistration,
        registrationFormId,
        requiredForms: sortedForms,
        currentFormId: null,
        currentFormIndex: 0,
        totalForms: 0,
        isError: true,
        errorMessage:
          'Unable to load forms. Please go back and select a service.',
      };
    }

    // Calculate total forms
    // If user needs registration, it counts as form 1
    // Then add all required forms from service
    let totalForms = sortedForms.length;
    if (needsRegistration && registrationFormId) {
      totalForms += 1;
    }

    // Determine current form
    let currentFormId: string | null = null;
    let currentFormIndex = 0;

    // Check if registration is needed and not completed
    if (
      needsRegistration &&
      registrationFormId &&
      !completedFormIds.has(registrationFormId)
    ) {
      currentFormId = registrationFormId;
      currentFormIndex = 1;
    } else {
      // Find first uncompleted required form
      const registrationOffset = needsRegistration && registrationFormId ? 1 : 0;

      for (let i = 0; i < sortedForms.length; i++) {
        if (!completedFormIds.has(sortedForms[i].formId)) {
          currentFormId = sortedForms[i].formId;
          currentFormIndex = registrationOffset + i + 1;
          break;
        }
      }
    }

    return {
      needsRegistration,
      registrationFormId,
      requiredForms: sortedForms,
      currentFormId,
      currentFormIndex,
      totalForms,
      isError: false,
      errorMessage: null,
    };
  }, [flowContext, completedFormIds, currentStep]);
}
